import type { CustomFile } from '../../types';

import type { TypeResolvedFeatures, TypeUploadOnceResult } from '../types';
import type { TypeAttemptFn, TypeUploadFeature, TypeUploadPart } from '../core/types';

// ----------------------------------------------------------------------------

export type TypeChunkFeatureDeps = {
    file: CustomFile;
    isAborted: () => boolean;
    /*
     * The caller's whole-file transform, applied here before slicing. Slicing the original and
     * transforming each piece would not be the same thing for anything stateful (a compressor, an
     * encryptor), so the transform runs once and the slices come out of its result.
     */
    fnPrepareFile?: (file: CustomFile) => Blob | Promise<Blob>;
};

// ----------------------------------------------------------------------------

/*
 * Cuts a blob into `size`-byte slices. The last one takes whatever is left, so the parts always add
 * up to the source exactly.
 */
function sliceBlob(blob: Blob, size: number): TypeUploadPart[] {
    const total = Math.max(1, Math.ceil(blob.size / size));
    const parts: TypeUploadPart[] = [];

    for (let index = 0; index < total; index++) {
        const start = index * size;
        const end = Math.min(start + size, blob.size);

        parts.push({ blob: blob.slice(start, end), index, total, start, end });
    }

    return parts;
}

// ----------------------------------------------------------------------------

/*
 * Sends one file as several requests, in order.
 *
 * Wraps the attempt and calls it once per slice, so it composes with whatever is already inside it.
 * Order matters and `createFeatures` sets it: retry sits closer to the request, which means a slice
 * that fails is retried on its own instead of restarting the whole file — the only reason chunking
 * is worth doing on a large upload.
 *
 * The wire format is not this feature's to decide. It slices, sequences and reports; how a server
 * recognises a chunk comes from the caller's `fnHeaders` and `fnChunkBody`.
 */
function createChunkFeature(features: TypeResolvedFeatures, deps: TypeChunkFeatureDeps): TypeUploadFeature | null {
    const { enabled, size, minSize } = features.chunk;

    if (!enabled || size <= 0) {
        return null;
    }

    /*
     * At or below the threshold the file goes as one request and the feature stays out entirely —
     * no part, so nothing downstream behaves differently from an unchunked upload.
     */
    if (deps.file.size <= minSize) {
        return null;
    }

    return {
        name: 'chunk',
        wrap:
            (next: TypeAttemptFn): TypeAttemptFn =>
            async (): Promise<TypeUploadOnceResult> => {
                const source = deps.fnPrepareFile ? await deps.fnPrepareFile(deps.file) : deps.file;
                const parts = sliceBlob(source, size);

                let lastResult: TypeUploadOnceResult = { ok: false, status: 0, reason: 'abort' };

                for (const part of parts) {
                    if (deps.isAborted()) {
                        return { ok: false, xhr: lastResult.xhr, status: 0, reason: 'abort' };
                    }

                    lastResult = await next(part);

                    /*
                     * Stop at the first slice that does not land. The parts are ordered and a server
                     * assembling them cannot use what follows a hole, so carrying on would only
                     * upload bytes it has to discard.
                     */
                    if (!lastResult.ok) {
                        return lastResult;
                    }
                }

                return lastResult;
            },
    };
}

// ----------------------------------------------------------------------------

export { sliceBlob };

export default createChunkFeature;
